import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';

function CoinIcon({ size = 48, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <circle cx="32" cy="32" r="30" fill="url(#coinGlow)" opacity="0.3"/>
      <circle cx="32" cy="32" r="28" fill="url(#coinBody)" stroke="url(#coinBorder)" strokeWidth="2"/>
      <circle cx="32" cy="32" r="22" fill="none" stroke="#FDE68A" strokeWidth="1" strokeDasharray="3 2"/>
      <ellipse cx="26" cy="22" rx="8" ry="5" fill="#FEF3C7" opacity="0.4" transform="rotate(-20 26 22)"/>
      <text x="32" y="40" textAnchor="middle" fontSize="22" fontWeight="900" fill="#92400E" fontFamily="Georgia, serif">$</text>
      <ellipse cx="32" cy="10" rx="12" ry="3" fill="#FEF3C7" opacity="0.5"/>
      <defs>
        <radialGradient id="coinGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FCD34D"/>
          <stop offset="100%" stopColor="#F59E0B" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id="coinBody" cx="35%" cy="30%" r="65%">
          <stop offset="0%" stopColor="#FDE68A"/>
          <stop offset="40%" stopColor="#F59E0B"/>
          <stop offset="100%" stopColor="#B45309"/>
        </radialGradient>
        <linearGradient id="coinBorder" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FDE68A"/>
          <stop offset="0.5" stopColor="#F59E0B"/>
          <stop offset="1" stopColor="#92400E"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

interface CoinPackage {
  id: number;
  price: string;
  coins: number;
  inGameCoins: number | null;
  bonusPct: number | null;
  noBonus: boolean;
  badge: string | null;
  highlight: boolean;
}

export default function CoinsShopPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 游戏内优惠：500无优惠, 1000+20%=1200, 2000+50%=3000, 5000+80%=9000, 10000+100%=20000, 20000+200%=60000
  // 官网再+20%：无优惠档不变，有优惠档×1.2
  const coinPackages: CoinPackage[] = [
    { id: 1, price: '$4.99',   coins: 500,   inGameCoins: null,  bonusPct: null, noBonus: true,  badge: 'Starter',    highlight: false },
    { id: 2, price: '$9.99',   coins: 1440,  inGameCoins: 1200,  bonusPct: 44,   noBonus: false, badge: null,         highlight: false },
    { id: 3, price: '$19.99',  coins: 3600,  inGameCoins: 3000,  bonusPct: 80,   noBonus: false, badge: 'Popular',    highlight: true  },
    { id: 4, price: '$49.99',  coins: 10800, inGameCoins: 9000,  bonusPct: 116,  noBonus: false, badge: 'Best Value', highlight: true  },
    { id: 5, price: '$99.99',  coins: 24000, inGameCoins: 20000, bonusPct: 140,  noBonus: false, badge: null,         highlight: false },
    { id: 6, price: '$199.99', coins: 72000, inGameCoins: 60000, bonusPct: 260,  noBonus: false, badge: 'Max Bonus',  highlight: true  },
  ];

  const formatNumber = (num: number) => {
    if (num >= 10000) return (num / 10000).toFixed(1).replace(/\.0$/, '') + 'W';
    if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return num.toString();
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-0 pb-0 overflow-hidden" style={{ minHeight: '400px' }}>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=shiny%20golden%20coins%20scattered%20and%20stacked%20on%20dark%20luxury%20surface%2C%20dramatic%20warm%20golden%20light%20rays%2C%20rich%20yellow%20and%20amber%20tones%2C%20cinematic%20moody%20atmosphere%2C%20treasure%20coins%20glowing%2C%20premium%20casino%20gold%20coins%20pile%2C%20photorealistic%20render%2C%20dark%20elegant%20background&width=1440&height=400&seq=coins-hero-bg-003&orientation=landscape"
            alt="Coins Store"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-zinc-950"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 pt-36 pb-16">
          <button
            onClick={() => navigate('/shop')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-black/40 border border-zinc-700 rounded-lg hover:border-yellow-500/60 transition-colors mb-8 cursor-pointer whitespace-nowrap backdrop-blur-sm"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            <span className="text-sm">Back to Shop</span>
          </button>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full mb-5 backdrop-blur-sm">
              <i className="ri-vip-crown-fill text-yellow-400 text-sm"></i>
              <span className="text-sm font-semibold text-yellow-300">Gold Coins Store</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black mb-4 bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-500 bg-clip-text text-transparent">
              Gold Coins Store
            </h1>
            <p className="text-base text-zinc-300 max-w-xl mx-auto">
              Exclusive website discount · Get approximately <strong className="text-yellow-400">20%</strong> more gold coins than in-game purchases
            </p>
          </div>
        </div>
      </section>

      {/* Coin Packages */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coinPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative bg-zinc-900 border rounded-2xl p-6 hover:shadow-xl hover:shadow-yellow-500/15 transition-all duration-300 cursor-pointer group hover:-translate-y-1 ${
                  pkg.highlight ? 'border-yellow-500/60 shadow-md shadow-yellow-500/10' : 'border-zinc-800 hover:border-yellow-500/40'
                }`}
              >
                {pkg.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full shadow-lg z-10">
                    <span className="text-xs font-bold text-white whitespace-nowrap">{pkg.badge}</span>
                  </div>
                )}

                {/* Coin Icon */}
                <div className="flex items-center justify-center mb-4 mt-2">
                  <div className="group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300 drop-shadow-lg">
                    <CoinIcon size={60} />
                  </div>
                </div>

                {/* Price */}
                <div className="text-center mb-4">
                  <div className="text-3xl font-black text-white">{pkg.price}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">USD</div>
                </div>

                {/* Coins Amount */}
                <div className="text-center mb-4 py-4 bg-gradient-to-br from-yellow-500/10 to-amber-500/5 rounded-xl border border-yellow-500/20 group-hover:border-yellow-500/40 transition-colors">
                  <div className="text-3xl font-black bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent mb-1">
                    {formatNumber(pkg.coins)}
                  </div>
                  <div className="text-xs text-zinc-400">Gold Coins</div>
                </div>

                {/* Comparison or No Bonus */}
                {pkg.noBonus ? (
                  <div className="mb-4 p-3 bg-zinc-800/60 rounded-xl text-center">
                    <span className="text-xs text-zinc-400">Same as in-game</span>
                    <div className="text-xs text-zinc-500 mt-0.5">No extra bonus</div>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-zinc-800/60 rounded-xl space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400">In-game</span>
                      <span className="text-zinc-300 line-through">{pkg.inGameCoins !== null ? formatNumber(pkg.inGameCoins) : ''}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400">Website</span>
                      <span className="text-yellow-400 font-bold">{formatNumber(pkg.coins)}</span>
                    </div>
                  </div>
                )}

                {/* Bonus Badge */}
                <div className="mb-4 text-center">
                  {pkg.noBonus ? (
                    <div className="inline-flex items-center gap-1 px-3 py-1 bg-zinc-700/50 border border-zinc-600/50 rounded-full">
                      <span className="text-xs text-zinc-400">No extra bonus</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-500/15 border border-green-500/30 rounded-full">
                      <i className="ri-gift-fill text-green-400 text-xs"></i>
                      <span className="text-xs font-bold text-green-400">+{pkg.bonusPct}% Bonus</span>
                    </div>
                  )}
                </div>

                {/* Buy Button */}
                <button className="w-full py-3 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl font-bold text-white hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300 whitespace-nowrap text-sm">
                  Purchase Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 px-4 bg-zinc-900/40 border-t border-zinc-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: 'Secure Payment', sub: 'Secure Payment Protection' },
              { icon: 'ri-flashlight-line', title: 'Instant Delivery', sub: 'Instant Delivery' },
              { icon: 'ri-customer-service-2-line', title: '24/7 Support', sub: '24/7 Customer Support' },
            ].map((f) => (
              <div key={f.title} className="text-center p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
                <div className="w-12 h-12 rounded-full bg-yellow-500/15 border border-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                  <i className={`${f.icon} text-xl text-yellow-400`}></i>
                </div>
                <h3 className="text-base font-bold mb-1">{f.title}</h3>
                <p className="text-sm text-zinc-400">{f.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-base font-semibold mb-4 text-yellow-400">Shop</h4>
              <ul className="space-y-2">
                <li><a href="/shop/chips" className="text-sm text-zinc-400 hover:text-yellow-400 transition-colors cursor-pointer">Chips Store</a></li>
                <li><a href="/shop/coins" className="text-sm text-zinc-400 hover:text-yellow-400 transition-colors cursor-pointer">Gold Coins Store</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-yellow-400">Support</h4>
              <ul className="space-y-2">
                <li><a href="https://t.me/AmagiPoker" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-400 hover:text-yellow-400 transition-colors cursor-pointer">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-yellow-400">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/privacy" className="text-sm text-zinc-400 hover:text-yellow-400 transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="/terms" className="text-sm text-zinc-400 hover:text-yellow-400 transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="/license" className="text-sm text-zinc-400 hover:text-yellow-400 transition-colors cursor-pointer">License Agreement</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-yellow-400">Download</h4>
              <div className="flex gap-3">
                <a href="https://apps.apple.com/us/app/amagi-poker-texas-holdem/id6749308770" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:border-yellow-400 transition-colors cursor-pointer" title="App Store">
                  <i className="ri-apple-fill text-lg"></i>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.GAMEINCAMAGI.POKERAGG2D" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:border-yellow-400 transition-colors cursor-pointer" title="Google Play">
                  <i className="ri-google-play-fill text-lg"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-800 text-center">
            <p className="text-sm text-zinc-500">© 2025 Amagi Poker. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
